import React, { useMemo } from 'react';
import './AppIcon.scss';
import type { AppIcon as AppIconType } from '../../types/apps';
import { Icon as IconifyIcon } from '@iconify/react';
import { usePhosphorIcon } from '../../hooks/usePhosphorIcon';

interface AppIconProps {
  icon: string | AppIconType;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  className?: string;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
  /** Bare icon with no decorative container — drops the image type's built-in
   * rounded-corner + drop-shadow "chiplet" look so callers (e.g. AppSquircle's
   * flat mode) can render just the raw glyph. Only affects the `image` type. */
  plain?: boolean;
}

export const AppIcon: React.FC<AppIconProps> = ({ 
  icon, 
  size = 'medium', 
  className = '',
  width,
  height,
  style: customStyle,
  plain = false,
}) => {
  const iconConfig = useMemo(() => {
    // Handle legacy string icons
    if (typeof icon === 'string') {
      return { type: 'emoji' as const, emoji: icon };
    }
    return icon;
  }, [icon]);

  const style = useMemo(() => {
    const baseStyle: React.CSSProperties = {};
    
    // Apply custom dimensions if provided (with !important via cssText hack if needed)
    if (width !== undefined) {
      baseStyle.width = typeof width === 'number' ? `${width}px` : width;
    }
    if (height !== undefined) {
      baseStyle.height = typeof height === 'number' ? `${height}px` : height;
    }
    
    // Apply gradient styling if needed
    if (iconConfig.type === 'gradient' && iconConfig.gradient) {
      baseStyle.background = iconConfig.gradient;
      baseStyle.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)';
    }
    
    // Merge with custom style prop
    return { ...baseStyle, ...customStyle };
  }, [iconConfig, width, height, customStyle]);

  // Determine if this should use app styling (chiplet) or entity styling (raw icon)
  const useAppStyling = useMemo(() => {
    // Check metadata first
    if (iconConfig.metadata) {
      return iconConfig.metadata.isAppIcon === true;
    }
    // Default to entity styling (no chiplet) if no metadata
    return false;
  }, [iconConfig.metadata]);

  const baseClass = useAppStyling ? 'app-icon' : 'entity-icon';
  const sizeClass = useAppStyling ? `app-icon--${size}` : `entity-icon--${size}`;
  const gradientClass = iconConfig.type === 'gradient' ? (useAppStyling ? 'app-icon--gradient' : 'entity-icon--gradient') : '';

  const PhosphorIconComponent = usePhosphorIcon(
    iconConfig.type === 'phosphor' ? iconConfig.phosphorIcon : undefined
  );

  return (
    <span 
      className={`${baseClass} ${sizeClass} ${className} ${gradientClass}`}
      style={style}
    >
      {(iconConfig.type === 'emoji' || (iconConfig.type === 'gradient' && iconConfig.emoji)) && iconConfig.emoji}
      {iconConfig.type === 'image' && (iconConfig.imagePath || iconConfig.src) && (
        <img 
          src={iconConfig.imagePath || iconConfig.src} 
          alt="App icon" 
          style={plain
            ? { width: '100%', height: '100%', objectFit: 'contain' }
            : { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20%', boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)' }}
        />
      )}

      {iconConfig.type === 'phosphor' && iconConfig.phosphorIcon && (
        (() => {
          if (PhosphorIconComponent === undefined) {
            // Icon module still loading (lazy-loaded — see usePhosphorIcon).
            return null;
          }
          if (!PhosphorIconComponent) {
            console.warn('[AppIcon] Phosphor icon not found:', iconConfig.phosphorIcon);
            return <span>⚠️</span>;
          }
          
          try {
            return (
              <PhosphorIconComponent 
                weight={iconConfig.phosphorWeight || 'regular'}
                style={{ 
                  width: '100%', 
                  height: '100%',
                  color: iconConfig.color || 'currentColor'
                }}
              />
            );
          } catch (error) {
            console.error('[AppIcon] Phosphor icon render failed:', error, {
              iconName: iconConfig.phosphorIcon,
              weight: iconConfig.phosphorWeight
            });
            return <span>⚠️</span>;
          }
        })()
      )}

      {iconConfig.type === 'component' && iconConfig.component && (
        (() => {
          const Component = iconConfig.component;
          
          try {
            return (
              <Component 
                {...iconConfig.componentProps}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              />
            );
          } catch (error) {
            console.error('[AppIcon] Component render failed:', error, {
              component: Component,
              props: iconConfig.componentProps
            });
            return <span>⚠️</span>; // Fallback if component fails
          }
        })()
      )}

      {iconConfig.type === 'svg' && (iconConfig.svgContent || iconConfig.svgPath) && (
        (() => {
          try {
            if (iconConfig.svgContent) {
              return (
                <div 
                  dangerouslySetInnerHTML={{ __html: iconConfig.svgContent }}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: iconConfig.color || 'currentColor'
                  }}
                />
              );
            } else if (iconConfig.svgPath) {
              return (
                <img 
                  src={iconConfig.svgPath} 
                  alt="SVG icon" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                    filter: iconConfig.color ? `drop-shadow(0 0 0 ${iconConfig.color})` : undefined
                  }}
                />
              );
            }
          } catch (error) {
            console.error('[AppIcon] SVG render failed:', error, {
              svgContent: iconConfig.svgContent?.substring(0, 100),
              svgPath: iconConfig.svgPath
            });
            return <span>⚠️</span>;
          }
        })()
      )}

      {iconConfig.type === 'iconify' && iconConfig.iconifyIcon && (
        (() => {
          // For simple-icons (brand icons), use native colors by not setting color
          const isSimpleIcon = iconConfig.iconifyIcon.startsWith('simple-icons:');
          const style: React.CSSProperties = {
            width: '100%',
            height: '100%',
          };
          
          // Only set color if it's explicitly provided, or if it's not a simple-icon
          if (iconConfig.color) {
            style.color = iconConfig.color;
          } else if (!isSimpleIcon) {
            style.color = 'currentColor';
          }
          // For simple-icons, don't set color at all - let it use native brand colors
          
          return <IconifyIcon icon={iconConfig.iconifyIcon} style={style} />;
        })()
      )}
    </span>
  );
}; 